"use client";
import React, { useState } from 'react';
import { ChevronDown } from 'lucide-react';
import JsonLd from '@/components/JsonLd';
import { getFaqSchema } from '@/lib/structuredData';

const faqData = [
    {
        question: "How does a data-driven strategy actually improve marketing ROI?",
        answer: "In multiple client engagements, we’ve seen brands reduce wasted ad spend by up to 40% after implementing structured tracking and analytics frameworks. By mapping every campaign to measurable KPIs and continuously optimizing based on performance data, marketing budgets shift from experimentation to predictable investment."
    },
    {
        question: "What happens when a business scales without a systematic marketing framework?",
        answer: "Companies that scale without structured systems often face inconsistent lead quality and rising acquisition costs. In one engagement, reorganizing fragmented campaigns into a unified performance framework stabilized lead flow and created repeatable growth channels within three months."
    },
    {
        question: "How do you identify which channels actually drive revenue?",
        answer: "We implement cross-channel attribution models that track the full customer journey. This approach has helped clients uncover hidden high-performing channels and reallocate budgets toward revenue-producing touchpoints instead of vanity metrics."
    },
    {
        question: "Can website redesign alone improve business performance?",
        answer: "Yes — when guided by behavioral data. A conversion-focused redesign using analytics insights has increased qualified inquiries by over 2× for several clients by removing friction in the user journey and optimizing funnels."
    },
    {
        question: "How important is continuous optimization after launching campaigns?",
        answer: "Campaigns that remain static lose efficiency quickly. Through ongoing A/B testing and real-time monitoring, we've helped brands maintain consistent performance improvements month over month instead of experiencing performance plateaus."
    }
];

const FAQItem = ({ question, answer, isOpen, onClick }: { question: string; answer: string; isOpen: boolean; onClick: () => void }) => {
    return (
        <div
            className={`group mb-4 rounded-xl transition-all duration-300 ${isOpen ? 'bg-[#3730a3]/10 shadow-md' : 'bg-[#3730a3]/5 hover:bg-[#3730a3]/8'
                } border-l-4 border-[#3730a3]/0 ${isOpen ? 'border-[#3730a3]' : ''}`}
        >
            <button
                onClick={onClick}
                className="w-full flex items-center justify-between p-4 md:p-6 text-left focus:outline-none"
            >
                <span className="text-base md:text-xl font-semibold text-gray-900 pr-4 md:pr-8">
                    {question}
                </span>
                <div className={`shrink-0 w-10 h-10 rounded-full flex items-center justify-center transition-all duration-300 ${isOpen ? 'bg-[#3730a3]' : 'bg-[#3730a3]/15 group-hover:bg-[#3730a3]/25'
                    }`}>
                    <ChevronDown className={`w-6 h-6 transition-transform duration-300 ${isOpen ? 'rotate-180 text-white' : 'text-[#3730a3]'
                        }`} />
                </div>
            </button>
            <div
                className={`overflow-hidden transition-all duration-300 ease-in-out ${isOpen ? 'max-h-[500px] opacity-100 pb-4 md:pb-6 px-4 md:px-6' : 'max-h-0 opacity-0'
                    }`}
            >
                <p className="text-gray-700 leading-relaxed max-w-4xl">
                    {answer}
                </p>
            </div>
        </div>
    );
};

export default function FAQSection() {
    const [openIndex, setOpenIndex] = useState(0);

    return (
        <section className="relative py-16 md:py-24 bg-gray-50 px-4 md:px-24 rounded-t-[2rem] md:rounded-t-[4rem] overflow-hidden z-10" style={{ boxShadow: '0 -20px 50px rgba(0,0,0,0.2)' }} data-theme="light">
            <JsonLd data={getFaqSchema(faqData)} />
            <div className="max-w-5xl mx-auto">
                {/* Subtitle */}
                <div className="text-center mb-4">
                    <span className="inline-block px-4 py-1.5 rounded-full bg-[#3730a3]/10 text-[#3730a3] text-[10px] md:text-xs font-bold tracking-widest uppercase">
                        Strategy • Data • Growth
                    </span>
                </div>

                {/* Title */}
                <h2 className="text-3xl md:text-5xl lg:text-6xl font-black text-gray-900 text-center mb-10 md:mb-16 tracking-tight" style={{ fontFamily: "var(--font-clash), 'Clash Display', sans-serif" }}>
                    Frequently Asked Questions
                </h2>

                {/* FAQ Items */}
                <div className="space-y-2">
                    {faqData.map((item, index) => (
                        <FAQItem
                            key={index}
                            question={item.question}
                            answer={item.answer}
                            isOpen={openIndex === index}
                            onClick={() => setOpenIndex(openIndex === index ? -1 : index)}
                        />
                    ))}
                </div>
            </div>
        </section>
    );
}
